import { formatDateShort } from '@ardoq/date-time';
import { zIndex } from '@ardoq/design-tokens';
import { Field } from '@ardoq/forms';
import {
  getDefaultOffscreenPlacement,
  getRelativePlacementCSS,
  useEvent,
} from '@ardoq/hooks';
import { ARDOQ_DEFAULT_LOCALE } from '@ardoq/locale';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale } from '@ardoq/i18n';
import { PopoverContentType } from '@ardoq/popovers';
import { noop } from 'lodash';
import {
  CSSProperties,
  KeyboardEvent,
  KeyboardEventHandler,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { DateInput, useMultipleRefs } from './DateInput';
import { DateTimePickerDropdown } from './DateTimePickerDropdown';
import {
  DatePickerCloseTriggerKeys,
  DateTimeInputSpecificProps,
  DateTimePickerConfig,
} from './types';
import { useScrollLock } from './useScrollLock';
import { getAdjustedDropdownPosition } from './utils';

export type DateTimePickerProps = {
  date?: Date | null;
  onChange?: (newDate: Date | null) => void;
  onCloseDatepicker?: VoidFunction;
  isDisabled?: boolean;
  config?: DateTimePickerConfig;
  autoFocus?: boolean;
  isReadOnly?: boolean;
  popoverContent?: PopoverContentType;
  popoverHelpContent?: ReactNode;
  additionalDropdownOffset?: { left?: number; top?: number };
  forceOpen?: boolean;
  changeValueOnInvalidDate?: boolean;
  /**
   * Fires when a value is chosen through the calendar UI (a day, "today", or
   * "clear") — as opposed to typing into the input. Lets a consumer treat a
   * calendar pick as a commit while leaving typed input to be committed
   * explicitly for example via clicking Save.
   */
  onCalendarSelect?: VoidFunction;
  /**
   * Fires as the user edits the input text, reporting whether it differs from
   * the committed value. Lets a consumer reflect a pending typed edit (e.g.
   * enable a Save button) before the value is committed on blur/Enter.
   */
  onModifiedChange?: (isModified: boolean) => void;
  customCloseHandlers?: Set<VoidFunction>;
  helperText?: ReactNode;
  /*
   * If the dropdown is rendered in other place than the input field,
   * the onFocus needs reference to the container with the dropdown so it knows when to close it.
   */
  popupParent?: HTMLElement | null;
  /*
   * Renders the dropdown in a portal attached to document.body, escaping any
   * overflow-hidden or stacking-context ancestors. Use this when the picker is
   * inside a clipping container such as a sidebar with overflow:hidden.
   * We track this explicitly so we know which call sites need it and can
   * migrate them when we replace the dropdown with popover2025.
   */
  renderInPortal?: boolean;
  /** @deprecated onBlur overrides the internal input blur and breaks typing dates manually — use {@link DateTimePickerProps.onCloseDatepicker onCloseDatepicker}. */
  onBlur?: never;
  i18nLocale?: I18nLocale;
} & DateTimeInputSpecificProps;

const inc = (value: number) => value + 1;

type CSS = CSSProperties | null;
type RefFn = (node: any) => void;

function useRelativePlacement(): [CSS, RefFn, RefFn] {
  const scrollEvent = useEvent('scroll', true);
  const resizeEvent = useEvent('resize');
  const [css, setCss] = useState<CSS>(null);
  const [elementInstanceId, setElementInstanceId] = useState<number>(0);
  const [referenceInstanceId, setReferenceInstanceId] = useState<number>(0);
  const elementToBePlacedPrivateRef = useRef<Element | null>(null);
  const referenceElementPrivateRef = useRef<Element | null>(null);
  const elementToBePlacedRef = useCallback((node: Element) => {
    elementToBePlacedPrivateRef.current = node;
    setElementInstanceId(inc);
  }, []);
  const referenceElementRef = useCallback((node: Element) => {
    referenceElementPrivateRef.current = node;
    setReferenceInstanceId(inc);
  }, []);
  useEffect(() => {
    const elementBox =
      elementToBePlacedPrivateRef.current?.getBoundingClientRect();
    const referenceBox =
      referenceElementPrivateRef.current?.getBoundingClientRect();
    if (!elementBox || !referenceBox) {
      setCss(null);
      return;
    }
    setCss(getRelativePlacementCSS(elementBox, referenceBox));
  }, [elementInstanceId, referenceInstanceId, scrollEvent, resizeEvent]);

  return [css, elementToBePlacedRef, referenceElementRef];
}

const useFocus = (
  container: HTMLElement | null,
  focusRef: RefObject<HTMLElement | null>,
  onBlur?: VoidFunction,
  portalRef?: RefObject<HTMLElement | null>
): [boolean, VoidFunction, VoidFunction] => {
  const [hasFocus, setHasFocus] = useState(false);

  useEffect(() => {
    const focusEventHandler = () => {
      if (hasFocus) {
        const isInsideContainer = container?.contains(document.activeElement);
        const isInsidePortal = portalRef?.current?.contains(
          document.activeElement
        );
        if (!isInsideContainer && !isInsidePortal) {
          setHasFocus(false);
          onBlur?.();
        } else {
          focusRef.current?.focus();
        }
      }

      if (document.activeElement === focusRef.current) {
        setHasFocus(true);
      }
    };

    document.addEventListener('focusin', focusEventHandler);

    return () => document.removeEventListener('focusin', focusEventHandler);
  }, [container, focusRef, onBlur, hasFocus, portalRef]);

  const focus = useCallback(() => {
    focusRef.current?.focus();
    setHasFocus(true);
  }, [focusRef, setHasFocus]);

  const blur = useCallback(() => {
    focusRef.current?.blur();
    setHasFocus(false);
  }, [focusRef, setHasFocus]);

  return [hasFocus, focus, blur];
};

const DateTimePicker = ({
  date = null,
  onChange = noop,
  onCloseDatepicker = noop,
  isDisabled,
  config = {},
  errorMessage,
  additionalDropdownOffset,
  forceOpen,
  onCalendarSelect,
  customCloseHandlers = new Set(),
  popupParent,
  renderInPortal = false,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  ...restInputProps
}: DateTimePickerProps) => {
  const [lockScrollingTo, unlockScrolling] = useScrollLock();
  const [datepickerCss, datepickerRef, inputPlacementRef] =
    useRelativePlacement();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const inputRefs = useMultipleRefs(inputRef, inputPlacementRef);
  const dropdownRefs = useMultipleRefs(datepickerRef, portalRef);

  const [closeHandlers] = useState(new Set<VoidFunction>());
  const [closeOnKeyHandlers] = useState(
    new Set<KeyboardEventHandler<HTMLInputElement>>()
  );

  const onBlur = useCallback(() => {
    for (const handler of closeHandlers) handler();
    for (const handler of customCloseHandlers) handler();
    onCloseDatepicker();
  }, [closeHandlers, customCloseHandlers, onCloseDatepicker]);

  const [isOpen, openDatepicker, closeDatepicker] = useFocus(
    popupParent ?? containerRef.current,
    inputRef,
    onBlur,
    renderInPortal ? portalRef : undefined
  );

  const closeOnDropdownNavigation = useCallback(() => {
    closeDatepicker();
    onCloseDatepicker();
    onCalendarSelect?.();
  }, [closeDatepicker, onCloseDatepicker, onCalendarSelect]);

  const closeOnKeyTrigger = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (Object.values<string>(DatePickerCloseTriggerKeys).includes(e.key)) {
        for (const handler of closeOnKeyHandlers) handler(e);

        closeDatepicker();
        onCloseDatepicker();
      }
    },
    [closeOnKeyHandlers, closeDatepicker, onCloseDatepicker]
  );

  if (forceOpen || isOpen) {
    lockScrollingTo(containerRef);
  } else {
    unlockScrolling(containerRef);
  }

  const getDateTimePickerDropdownCoords = useCallback(() => {
    const defaultPlacement = getDefaultOffscreenPlacement();
    if (!datepickerCss) return defaultPlacement;
    const left = datepickerCss?.left
      ? getAdjustedDropdownPosition(
          datepickerCss.left,
          additionalDropdownOffset?.left
        )
      : defaultPlacement.left;
    const top = datepickerCss?.top
      ? getAdjustedDropdownPosition(
          datepickerCss.top,
          additionalDropdownOffset?.top
        )
      : defaultPlacement.top;
    return {
      ...datepickerCss,
      left,
      top,
    };
  }, [
    additionalDropdownOffset?.left,
    additionalDropdownOffset?.top,
    datepickerCss,
  ]);

  const { formatter = formatDateShort, locale = ARDOQ_DEFAULT_LOCALE } = config;

  const dropdown = (forceOpen || isOpen) && !restInputProps.isReadOnly && (
    <DateTimePickerDropdown
      ref={renderInPortal ? dropdownRefs : datepickerRef}
      date={date}
      onChange={onChange}
      onSelectToday={closeOnDropdownNavigation}
      onSelectDay={closeOnDropdownNavigation}
      onClear={closeOnDropdownNavigation}
      config={{
        ...config,
        containerStyle: {
          width: '302px',
          height: 'fit-content', // to remove scroll from date-picker body
          zIndex: zIndex.DATE_PICKER,
          position: 'fixed',
          ...getDateTimePickerDropdownCoords(),
        },
      }}
      i18nLocale={i18nLocale}
    />
  );

  return (
    <Field
      ref={containerRef}
      tabIndex={-1}
      onKeyDown={closeOnKeyTrigger}
      dataTestId="date-time-picker-focus-field"
    >
      <DateInput
        id={restInputProps.inputId}
        key={date?.getTime()}
        ref={inputRefs}
        value={formatter(date, locale)}
        locale={locale}
        openDatepicker={openDatepicker}
        saveDate={onChange}
        errorMessage={errorMessage}
        isDisabled={isDisabled}
        config={config}
        closeHandlers={closeHandlers}
        closeOnKeyHandlers={closeOnKeyHandlers}
        i18nLocale={i18nLocale}
        {...restInputProps}
      />
      {renderInPortal && dropdown
        ? createPortal(dropdown, document.body)
        : dropdown}
    </Field>
  );
};

export { DateTimePicker };
